/**
 * Workload Tracking CRUD Service - Phase 4 Architecture
 *
 * Encapsulates CRUD operations for workload tracking entries.
 * Extracted from WorkloadTrackingService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import { getMyceliumTransport } from "@flowstate/core";
import type {
  CreateWorkloadTrackingRequest,
  UpdateWorkloadTrackingRequest,
  WorkloadTracking,
} from "@/types/time-management";
import { WorkloadTrackingValidationService } from "./workload-tracking-validation.service";

export class WorkloadTrackingCrudService implements IService {
  public readonly name = "WorkloadTrackingCrudService";
  private readonly supabase = typedSupabase;
  private readonly WORKLOAD_TABLE = "workload_tracking" as const;
  private validationService: WorkloadTrackingValidationService;

  constructor(validationService: WorkloadTrackingValidationService) {
    this.validationService = validationService;
  }

  async initialize(): Promise<void> {
    logger.info("✅ WorkloadTrackingCrudService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ WorkloadTrackingCrudService disposed");
  }

  async createWorkloadEntry(
    userId: string,
    request: CreateWorkloadTrackingRequest
  ): Promise<ServiceResponse<WorkloadTracking>> {
    try {
      const validationResult = await this.validationService.validateWorkloadData({
        id: "temp",
        user_id: userId,
        ...request,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as WorkloadTracking);

      if (validationResult.error) {
        return createErrorResponse(validationResult.error);
      }

      if (!validationResult.data?.isValid) {
        return createErrorResponse(
          validationResult.data?.warnings?.join(", ") ?? "Validation failed"
        );
      }

      const existingEntry = await this.getWorkloadEntry(userId, request.date);
      if (existingEntry.data) {
        return createErrorResponse(`Workload entry already exists for ${request.date}`);
      }

      const { data, error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .insert({
          user_id: userId,
          date: request.date,
          planned_hours: request.planned_hours ?? 0,
          actual_hours: request.actual_hours ?? 0,
          task_count: request.task_count ?? 0,
          completed_tasks: request.completed_tasks ?? 0,
          focus_time_minutes: request.focus_time_minutes ?? 0,
          interruption_count: request.interruption_count ?? 0,
          productivity_score: request.productivity_score,
          notes: request.notes,
        })
        .select()
        .single();

      if (error) {
        return createErrorResponse(error.message);
      }

      await getMyceliumTransport().publish({
        domain: "workload",
        type: "workload_created",
        userId,
        payload: { source: this.name, entry: data },
      });

      return createSuccessResponse(data as WorkloadTracking);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async getWorkloadEntry(
    userId: string,
    date: string
  ): Promise<ServiceResponse<WorkloadTracking | null>> {
    try {
      const { data, error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .select("*")
        .eq("user_id", userId)
        .eq("date", date)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return createSuccessResponse(null);
        }
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(data as WorkloadTracking);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async getUserWorkload(
    userId: string,
    dateRange: { start: string; end: string }
  ): Promise<ServiceResponse<WorkloadTracking[]>> {
    try {
      const { data, error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .select("*")
        .eq("user_id", userId)
        .gte("date", dateRange.start)
        .lte("date", dateRange.end)
        .order("date", { ascending: true });

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse((data ?? []) as WorkloadTracking[]);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async updateWorkloadEntry(
    entryId: string,
    updates: UpdateWorkloadTrackingRequest
  ): Promise<ServiceResponse<WorkloadTracking>> {
    try {
      const { data, error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .update(updates)
        .eq("id", entryId)
        .select()
        .single();

      if (error) {
        return createErrorResponse(error.message);
      }

      await getMyceliumTransport().publish({
        domain: "workload",
        type: "workload_updated",
        userId: data?.user_id,
        payload: { source: this.name, entryId, entry: data },
      });

      return createSuccessResponse(data as WorkloadTracking);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async deleteWorkloadEntry(entryId: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .delete()
        .eq("id", entryId);

      if (error) {
        return createErrorResponse(error.message);
      }

      await getMyceliumTransport().publish({
        domain: "workload",
        type: "workload_deleted",
        payload: { source: this.name, entryId },
      });

      return createSuccessResponse(undefined);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async createMultipleEntries(
    userId: string,
    entries: CreateWorkloadTrackingRequest[]
  ): Promise<ServiceResponse<WorkloadTracking[]>> {
    try {
      const insertData = entries.map((entry) => ({
        user_id: userId,
        date: entry.date,
        planned_hours: entry.planned_hours ?? 0,
        actual_hours: entry.actual_hours ?? 0,
        task_count: entry.task_count ?? 0,
        completed_tasks: entry.completed_tasks ?? 0,
        focus_time_minutes: entry.focus_time_minutes ?? 0,
        interruption_count: entry.interruption_count ?? 0,
        productivity_score: entry.productivity_score,
        notes: entry.notes,
      }));

      const { data, error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .insert(insertData)
        .select();

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse((data ?? []) as WorkloadTracking[]);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async upsertWorkloadEntry(
    userId: string,
    date: string,
    data: CreateWorkloadTrackingRequest
  ): Promise<ServiceResponse<WorkloadTracking>> {
    try {
      const existingEntry = await this.getWorkloadEntry(userId, date);

      if (existingEntry.data) {
        return await this.updateWorkloadEntry(existingEntry.data.id, data);
      } else {
        return await this.createWorkloadEntry(userId, { ...data, date });
      }
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async archiveOldEntries(beforeDate: string): Promise<ServiceResponse<number>> {
    try {
      const { error } = await this.supabase
        .from(this.WORKLOAD_TABLE)
        .delete()
        .lt("date", beforeDate);

      if (error) {
        return createErrorResponse(error.message);
      }

      return createSuccessResponse(0);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : "Unknown error");
    }
  }

  async aggregateHistoricalData(
    userId: string,
    aggregationType: "weekly" | "monthly" | "quarterly"
  ): Promise<ServiceResponse<void>> {
    logger.info(`Aggregating ${aggregationType} data for user ${userId}`);
    return createSuccessResponse(undefined);
  }
}
